import { useEffect, useState } from 'preact/hooks'
import clsx from 'clsx'

import { useFlickity } from 'hooks/useFlickity'

import { useAthosContext } from '../../fetchers/provider'
import { BaseResults } from './BaseResults'

export function CarouselResults({
  className,
  wrapperClassName = '',
  options = { pageDots: true, cellAlign: 'left' },
  productCardSettings,
  CardComponent,
  SkeletonComponent
}) {
  const { results, isLoading } = useAthosContext()
  const [skeletonNode, setSkeletonNode] = useState()
  const [realNode, setRealNode] = useState()
  const [containerNode, setContainerNode] = useState()
  const [showRealCarousel, setShowRealCarousel] = useState(false)

  useFlickity(skeletonNode, options, [])
  const { SliderPortal, flickity, inViewRange } = useFlickity(realNode, options, [results])

  const hasData = !isLoading && results?.results

  useEffect(() => {
    if (!hasData) {
      setShowRealCarousel(false)
      return
    }

    if (!inViewRange) {
      setShowRealCarousel(true)
      return
    }

    if (flickity && containerNode) {
      // Hold the skeleton's height while Flickity lays the real cells out, so the swap does not jump.
      containerNode.style.height = `${containerNode.getBoundingClientRect().height}px`
      flickity.resize()
      flickity.reloadCells()

      const timer = setTimeout(() => {
        containerNode.style.height = ''
        setShowRealCarousel(true)
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [hasData, flickity, containerNode, inViewRange])

  const trackClass = clsx('flex w-full', options.pageDots && 'pb-6')

  return (
    <BaseResults
      className={className}
      wrapperClassName={wrapperClassName}
      productCardSettings={productCardSettings}
      {...(CardComponent && { CardComponent })}
      {...(SkeletonComponent && { SkeletonComponent })}
      renderWrapper={(skeletonItems, realItems) => (
        <div ref={setContainerNode} className="relative grid w-full grid-cols-1 grid-rows-1 transition-all">
          <div
            className={clsx(
              'col-start-1 row-start-1 w-full transition-opacity',
              showRealCarousel ? 'pointer-events-none opacity-0' : 'opacity-100'
            )}
          >
            <div ref={setSkeletonNode} className={trackClass}>
              {skeletonItems}
            </div>
          </div>

          <div
            className={clsx(
              'col-start-1 row-start-1 w-full transition-opacity',
              showRealCarousel ? 'opacity-100' : 'pointer-events-none opacity-0'
            )}
          >
            <div ref={setRealNode} className={trackClass}>
              <SliderPortal>{realItems}</SliderPortal>
            </div>
          </div>
        </div>
      )}
    />
  )
}
